import BackendService from 'BackendService';
import { useEffect, useState } from 'react';
import Contact from '../../../models/Contact';
import Investor from '../../../models/Investor';
import { OverviewMetrics, ProjectionEvent } from './contactOverviewTypes';

export function useContactOverviewData(contactId: string | undefined) {
  const [contact, setContact] = useState<Contact | null>(null);
  const [contactLoading, setContactLoading] = useState(true);
  const [contactError, setContactError] = useState<string | undefined>();

  const [metrics, setMetrics] = useState<OverviewMetrics>({
    projectedRoePercent: null,
    projectedIrrPercent: null,
    moneyMultiple: null,
    totalInvested: null,
  });
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [committedInvestment, setCommittedInvestment] = useState<number | null>(null);
  const [completeFunds, setCompleteFunds] = useState(0);
  const [includeClosedFunds, setIncludeClosedFunds] = useState(false);

  const [events30, setEvents30] = useState<ProjectionEvent[]>([]);
  const [events90, setEvents90] = useState<ProjectionEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [show90, setShow90] = useState(false);

  const [investments, setInvestments] = useState<Investor[]>([]);
  const [investmentsLoading, setInvestmentsLoading] = useState(true);

  useEffect(() => {
    if (!contactId) return;
    setContactLoading(true);
    new BackendService()
      .getContact(contactId)
      .then(setContact)
      .catch((e) => setContactError(e?.message ?? 'Failed to load contact'))
      .finally(() => setContactLoading(false));
  }, [contactId]);

  useEffect(() => {
    if (!contactId) return;
    const bs = new BackendService();

    setMetricsLoading(true);
    Promise.all([
      bs.getContactInvestmentOverviewMetrics(contactId, { includeClosedFunds }),
      bs.getContactPortfolioSummary(contactId),
    ])
      .then(([m, ps]) => {
        setMetrics(m);
        setCommittedInvestment(ps?.portfolioSummary?.committedInvestment ?? null);
        setCompleteFunds(ps?.portfolioSummary?.completeFunds ?? 0);
      })
      .catch(console.error)
      .finally(() => setMetricsLoading(false));
  }, [contactId, includeClosedFunds]);

  useEffect(() => {
    if (!contactId) return;
    const bs = new BackendService();
    setEventsLoading(true);
    Promise.all([
      bs.getContactUpcomingEvents(contactId, 30),
      bs.getContactUpcomingEvents(contactId, 90),
    ])
      .then(([e30, e90]) => {
        setEvents30(e30?.items ?? []);
        setEvents90(e90?.items ?? []);
      })
      .catch(console.error)
      .finally(() => setEventsLoading(false));
  }, [contactId]);

  useEffect(() => {
    if (!contactId) return;
    setInvestmentsLoading(true);
    new BackendService()
      .getContactInvestments(contactId)
      .then((data) => setInvestments(data ?? []))
      .catch(console.error)
      .finally(() => setInvestmentsLoading(false));
  }, [contactId]);

  const eoiInvestors = investments.filter(
    (inv) => inv.eoiSubmitted || inv.status === 'LEAD' || inv.status === 'OPPORTUNITY',
  );
  const portfolioInvestors = investments.filter((inv) => inv.status === 'COMMITTED_INVESTOR');
  const displayEvents = show90 ? events90 : events30;

  return {
    contact,
    contactLoading,
    contactError,
    metrics,
    metricsLoading,
    committedInvestment,
    completeFunds,
    includeClosedFunds,
    setIncludeClosedFunds,
    eventsLoading,
    show90,
    setShow90,
    displayEvents,
    investmentsLoading,
    eoiInvestors,
    portfolioInvestors,
  };
}
